/**
 * Declarative field registry for the `v1/output/…` namespace.
 *
 * Why this exists
 * ---------------
 * The sandbox cut-over (ticket 86e2jbe3d) broke the app silently: field
 * names changed, Zod rejected every payload, the adapter returned null,
 * and drones vanished with no error anywhere in the UI or console. The
 * registry makes that class of failure visible instead of silent:
 *
 *   1. Every field we consume is ONE declarative row — adding a backend
 *      field is a one-line change, not a new branch in a mapper function.
 *   2. `applyRules` diffs the payload's actual keys against the registry,
 *      so any field the backend adds that we don't yet map is *recorded*
 *      rather than discarded. `getFieldReport()` surfaces it.
 *   3. Parse failures are counted per topic, so "we're receiving messages
 *      but rendering nothing" is observable in seconds.
 *
 * The report is read by the debug panel. Nothing here throws — a bad
 * payload must never take down the worker.
 */
import { collectPaths, getPath } from './paths';

/** One field mapping. `from` is a dot-path into the raw payload. */
export interface FieldRule<T> {
  /** Dot-path in the incoming payload, e.g. `ext.callsign`. */
  from: string;
  /** Key on the target envelope. Omit for fields we deliberately consume
   *  without writing to the envelope (e.g. `heightType`, which qualifies
   *  another field rather than becoming one). */
  to?: keyof T;
  /** Convert the raw value. Receives the whole payload as second arg so a
   *  rule can depend on a sibling field (`height` needs `heightType`). */
  via?: (value: unknown, payload: Record<string, unknown>) => unknown;
  /** Skip this rule unless the predicate passes. */
  when?: (payload: Record<string, unknown>) => boolean;
  /** Why this field is mapped the way it is — kept next to the mapping so
   *  the rationale can't drift away from the code. */
  note?: string;
}

/** Paths we knowingly receive and knowingly do not map. Listing them here
 *  keeps them out of the "unmapped" report without silently ignoring
 *  them — the intent is recorded, which is the whole point. */
export type IgnoredPaths = readonly string[];

export interface ApplyResult<T> {
  /** Fields successfully mapped onto the target envelope shape. */
  mapped: Partial<T>;
  /** Payload paths present on the wire that no rule and no ignore-entry
   *  covers. These are retained on the envelope's raw snapshot. */
  unmapped: string[];
}

/**
 * Run a rule set over a payload.
 *
 * A rule that throws is skipped and counted rather than aborting the
 * whole message — one malformed field must not cost us the other forty.
 */
export function applyRules<T>(
  topicKind: string,
  payload: Record<string, unknown>,
  rules: readonly FieldRule<T>[],
  ignored: IgnoredPaths = [],
): ApplyResult<T> {
  const mapped: Record<string, unknown> = {};
  const consumed = new Set<string>(ignored);

  for (const rule of rules) {
    consumed.add(rule.from);
    try {
      if (rule.when && !rule.when(payload)) continue;
      const raw = getPath(payload, rule.from);
      if (raw === undefined) continue;
      const value = rule.via ? rule.via(raw, payload) : raw;
      // null means "the payload had nothing usable here". Writing it would
      // put an explicit null in a partial, and `mergeNonNull` skips nulls
      // anyway — so omitting the key keeps partials honest about what they
      // actually carry.
      if (value === undefined || value === null) continue;
      if (rule.to) mapped[rule.to as string] = value;
    } catch {
      recordRuleError(topicKind, rule.from);
    }
  }

  // Anything on the wire that no rule and no ignore-entry accounts for.
  const unmapped: string[] = [];
  for (const path of collectPaths(payload)) {
    if (consumed.has(path)) continue;
    // A parent whose children are mapped isn't itself unmapped
    // (`ext` is covered by `ext.callsign`, `ext.missionName`, …).
    if (hasConsumedChild(path, consumed)) continue;
    // …and neither is a child of a consumed parent. Claiming
    // `operational_volume` claims its whole subtree — the rule parses the
    // structure itself rather than field by field.
    if (hasConsumedAncestor(path, consumed)) continue;
    unmapped.push(path);
  }

  if (unmapped.length > 0) recordUnmapped(topicKind, unmapped);

  return { mapped: mapped as Partial<T>, unmapped };
}

function hasConsumedChild(path: string, consumed: Set<string>): boolean {
  const prefix = `${path}.`;
  for (const c of consumed) if (c.startsWith(prefix)) return true;
  return false;
}

function hasConsumedAncestor(path: string, consumed: Set<string>): boolean {
  let cut = path.lastIndexOf('.');
  while (cut > 0) {
    if (consumed.has(path.slice(0, cut))) return true;
    cut = path.lastIndexOf('.', cut - 1);
  }
  return false;
}

/* ------------------------------------------------------------------ */
/*  Observability — read by the debug panel                            */
/* ------------------------------------------------------------------ */

interface TopicReport {
  /** Payload paths seen but not mapped → number of messages they arrived on. */
  unmapped: Map<string, number>;
  /** Rules whose `via` threw → count. */
  ruleErrors: Map<string, number>;
  /** Messages that failed schema validation entirely. */
  parseFailures: number;
  /** Most recent validation failure reason, for the debug panel. */
  lastParseError: string | null;
  /** Messages successfully turned into an envelope. */
  accepted: number;
}

const reports = new Map<string, TopicReport>();

function reportFor(topicKind: string): TopicReport {
  let r = reports.get(topicKind);
  if (!r) {
    r = {
      unmapped: new Map(),
      ruleErrors: new Map(),
      parseFailures: 0,
      lastParseError: null,
      accepted: 0,
    };
    reports.set(topicKind, r);
  }
  return r;
}

function recordUnmapped(topicKind: string, paths: string[]): void {
  const r = reportFor(topicKind);
  for (const p of paths) r.unmapped.set(p, (r.unmapped.get(p) ?? 0) + 1);
}

function recordRuleError(topicKind: string, from: string): void {
  const r = reportFor(topicKind);
  r.ruleErrors.set(from, (r.ruleErrors.get(from) ?? 0) + 1);
}

/** Call when a payload fails schema validation. This is the counter that
 *  would have made the sandbox cut-over obvious immediately. */
export function recordParseFailure(topicKind: string, reason: string): void {
  const r = reportFor(topicKind);
  r.parseFailures += 1;
  r.lastParseError = reason;
}

/** Call when a payload successfully produced an envelope. */
export function recordAccepted(topicKind: string): void {
  reportFor(topicKind).accepted += 1;
}

export interface FieldReportEntry {
  topic: string;
  accepted: number;
  parseFailures: number;
  lastParseError: string | null;
  unmapped: { path: string; count: number }[];
  ruleErrors: { from: string; count: number }[];
}

/** Snapshot of everything the registry has observed. Sorted so the
 *  noisiest unmapped fields surface first. */
export function getFieldReport(): FieldReportEntry[] {
  const out: FieldReportEntry[] = [];
  for (const [topic, r] of reports) {
    out.push({
      topic,
      accepted: r.accepted,
      parseFailures: r.parseFailures,
      lastParseError: r.lastParseError,
      unmapped: [...r.unmapped.entries()]
        .map(([path, count]) => ({ path, count }))
        .sort((a, b) => b.count - a.count),
      ruleErrors: [...r.ruleErrors.entries()]
        .map(([from, count]) => ({ from, count }))
        .sort((a, b) => b.count - a.count),
    });
  }
  return out.sort((a, b) => a.topic.localeCompare(b.topic));
}

/** Test hook — the report is module-level state. */
export function resetFieldReport(): void {
  reports.clear();
}

/* ------------------------------------------------------------------ */
/*  Shared converters                                                   */
/* ------------------------------------------------------------------ */

export function asNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Trim, and treat the empty string as "not supplied". The sandbox
 *  publisher sends `""` for model / drone_type / legal_profile rather
 *  than omitting them, and `""` must not overwrite a real value. */
export function asText(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : t;
}

export function asBool(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  return null;
}

/**
 * Accuracy fields are specified as ASTM enums in the FLYsafe MQTT doc
 * (`HA_10M`, `VA_25M`, `SA_3MPS`) but arrive as plain metres on the wire
 * (`0.3`) — doc open item OI-12. Accept both so a backend flip in either
 * direction can't blank the Navigation panel.
 */
export function accuracyMetres(v: unknown): number | null {
  const n = asNumber(v);
  if (n != null) return n;
  if (typeof v !== 'string') return null;
  const m = /^(?:HA|VA)_(\d+)M$/i.exec(v.trim());
  return m ? Number(m[1]) : null;
}

/** Speed accuracy enum (`SA_3MPS`) or plain m/s. */
export function speedAccuracyMps(v: unknown): number | null {
  const n = asNumber(v);
  if (n != null) return n;
  if (typeof v !== 'string') return null;
  const m = /^SA_(\d+)MPS$/i.exec(v.trim());
  return m ? Number(m[1]) : null;
}

/** Normalise an ISO timestamp to UTC "Z" form. The wire carries both
 *  `…Z` and `…+00:00`; our Zod schema accepts both but the round-trip
 *  keeps stored values comparable as strings. */
export function isoUtc(v: unknown): string | null {
  if (typeof v !== 'string' || v === '') return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}

/** Extract the trace-id from a W3C `traceparent`
 *  (`00-<trace-id>-<span-id>-<flags>`) so the debug panel can deep-link
 *  a stalled drone into Grafana/Tempo. */
export function traceId(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const parts = v.split('-');
  return parts.length >= 3 && parts[1] ? parts[1] : null;
}
